import {
  EmployeeFeedbackOutcome,
  EmployeeFeedbackState,
  FeedbackCycle,
  FeedbackCycleState,
  FinishEmployeeFeedbackOutcomeInput,
  MyPerformanceReviewsDocument,
  ReviewsListDocument,
  useAcceptEmployeeFeedbackOutcomeObjectivesMutation,
  useFinishEmployeeFeedbackOutcomeMutation,
} from '../../__generated__/graphql';
import { useMutationErrorHandler } from '../../../../hooks/useMutationErrorHandler';
import { FeedbackProps } from './types';
import { FeedbackStatusTypes } from './Reviews/types';

export const useReviewerSignAndFinish = () => {
  const errorHandler = useMutationErrorHandler();
  const [finishReview, { loading: isFinishing }] =
    useFinishEmployeeFeedbackOutcomeMutation({
      awaitRefetchQueries: true,
      /* eslint-disable-next-line no-restricted-syntax */
      refetchQueries: [
        {
          query: ReviewsListDocument,
        },
      ],
    });

  const signAndFinish = (
    input: Omit<FinishEmployeeFeedbackOutcomeInput, 'goals'>,
  ) => {
    return finishReview({
      variables: {
        input: {
          ...input,
          goals: [],
        },
      },
    })
      .then(() => {
        return Promise.resolve();
      })
      .catch(errorHandler);
  };

  return [isFinishing, signAndFinish] as const;
};

export const useEmployeeSignAndFinish = () => {
  const errorHandler = useMutationErrorHandler();

  const [acceptGoals, { loading: isFinishing }] =
    useAcceptEmployeeFeedbackOutcomeObjectivesMutation({
      awaitRefetchQueries: true,
      /* eslint-disable-next-line no-restricted-syntax */
      refetchQueries: [{ query: MyPerformanceReviewsDocument }],
    });

  const signAndFinish = (outcomeId: string) => {
    return acceptGoals({
      variables: {
        input: {
          employeeFeedbackOutcomeId: outcomeId,
        },
      },
    })
      .then(() => {
        return Promise.resolve();
      })
      .catch(errorHandler);
  };

  return [isFinishing, signAndFinish] as const;
};

// Structural shape so every surface deriving completion can share this helper,
// whatever query type its feedback comes from.
export type FeedbackCompletionProps = {
  outcome: Pick<EmployeeFeedbackOutcome, 'flair__Completed__c'>;
  feedbackCycle: Pick<FeedbackCycle, 'flair__State__c'>;
};

// Finishing a cycle in Salesforce leaves its outcomes untouched, so the cycle
// state is the only signal that its unended reviews are over (HAPPY-1625).
export const isFeedbackEnded = (feedback: FeedbackCompletionProps): boolean =>
  feedback.outcome.flair__Completed__c ||
  feedback.feedbackCycle.flair__State__c === FeedbackCycleState.Finished;

export type FeedbackSignatureProps = {
  flair__Is_Peer_Reviewer__c: boolean | null;
  outcome: Pick<EmployeeFeedbackOutcome, 'flair__Accepted_At__c'>;
  feedbackCycle: Pick<
    FeedbackCycle,
    'flair__State__c' | 'flair__Signature_required__c'
  >;
};

// An admin can finish a cycle at any point, including one nobody signed off, and
// those reviews now read as completed — so the sign action has to be gated on the
// cycle as well, or the employee gets asked to sign a review that is already shut.
export const canEmployeeSignFeedback = (
  feedback: FeedbackSignatureProps,
): boolean =>
  feedback.feedbackCycle.flair__State__c === FeedbackCycleState.InProgress &&
  feedback.feedbackCycle.flair__Signature_required__c &&
  !feedback.outcome.flair__Accepted_At__c &&
  !feedback.flair__Is_Peer_Reviewer__c;

export const isFeedbackInProgress = (feedback: FeedbackProps): boolean =>
  Boolean(
    feedback.flair__Progress__c ||
      feedback.employeeFeedback?.flair__Progress__c,
  );

export function feedbackStatus(feedback: FeedbackProps): FeedbackStatusTypes {
  const isStateSubmitted =
    feedback.flair__State__c === EmployeeFeedbackState.Submitted;

  if (isFeedbackEnded(feedback)) {
    return FeedbackStatusTypes.Completed;
  }

  if (
    feedback.feedbackCycle.flair__Meeting_Required__c &&
    isStateSubmitted &&
    feedback.employeeFeedback?.flair__State__c ===
      EmployeeFeedbackState.Submitted
  ) {
    return FeedbackStatusTypes.Meeting;
  }

  if (feedback.feedbackCycle.flair__Without_Reviewer__c && isStateSubmitted) {
    return FeedbackStatusTypes.Completed;
  }

  return isFeedbackInProgress(feedback)
    ? FeedbackStatusTypes.InProgress
    : FeedbackStatusTypes.NotStarted;
}
